"use client";

import { useState, useEffect, useRef } from "react";
import {
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Mail,
  RefreshCw,
  GraduationCap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5001";
const STUDENT_APP_URL = process.env.NEXT_PUBLIC_STUDENT_APP_URL || "http://localhost:5173";

interface ClassOption {
  id: string;
  name: string;
  grade: number | null;
  section: string | null;
}

function classLabel(c: ClassOption) {
  const parts = [];
  if (c.grade) parts.push(`Grade ${c.grade}`);
  if (c.section) parts.push(c.section);
  parts.push(c.name);
  return parts.join(" — ");
}

export default function SignupPage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    classId: "",
  });

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    fetch(`${API_URL}/api/student/auth/classes`)
      .then((r) => r.json())
      .then((data) => setClasses(data.data || []))
      .catch(() => setClasses([]))
      .finally(() => setLoadingClasses(false));
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  function handleField(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  }

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student/auth/signup/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to send OTP");
      } else {
        setStep(2);
        setResendCooldown(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleOtpInput(index: number, value: string) {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError("");
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the 6-digit OTP"); return; }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student/auth/signup/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp: code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Verification failed");
      } else {
        if (data.data?.token) {
          localStorage.setItem("student_token", data.data.token);
          localStorage.setItem("student_user", JSON.stringify(data.data.student));
        }
        window.location.href = `${STUDENT_APP_URL}/dashboard`;
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student/auth/signup/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to resend OTP");
      } else {
        setOtp(["", "", "", "", "", ""]);
        setResendCooldown(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F5F5] to-white">
      {/* Navbar */}
      <nav className="fixed top-0 z-50 w-full border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link href="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200">
              <GraduationCap className="h-5 w-5 text-gray-600" />
            </div>
            <Image src="/logo.png" alt="Phroneo" width={140} height={40} className="h-9 w-auto" />
          </Link>
          <p className="text-sm text-[#636363]">
            Already have an account?{" "}
            <a href={`${STUDENT_APP_URL}/login`} className="font-semibold text-[#0056D2] hover:underline">
              Log In
            </a>
          </p>
        </div>
      </nav>

      <div className="flex min-h-screen items-center justify-center px-4 pt-20 pb-12">
        <div className="w-full max-w-md">
          {/* Step indicator */}
          <div className="mb-8 flex items-center justify-center gap-3">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-all ${step === 1 ? "bg-[#0056D2] text-white" : "bg-[#00C58E] text-white"}`}>
              {step === 1 ? "1" : <CheckCircle2 className="h-4 w-4" />}
            </div>
            <div className={`h-0.5 w-16 transition-all ${step === 2 ? "bg-[#0056D2]" : "bg-gray-200"}`} />
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-all ${step === 2 ? "bg-[#0056D2] text-white" : "bg-gray-200 text-[#636363]"}`}>
              2
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            {step === 1 ? (
              <>
                <div className="mb-6 text-center">
                  <h1 className="text-2xl font-extrabold text-[#1F1F1F]">Create your account</h1>
                  <p className="mt-1.5 text-sm text-[#636363]">Join Phroneo and start learning with your AI teacher</p>
                </div>

                <form onSubmit={handleSendOtp} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#1F1F1F]">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleField}
                      placeholder="Enter your full name"
                      required
                      className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10"
                    />
                  </div>

                  {/* Class */}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#1F1F1F]">Select Class</label>
                    <select
                      name="classId"
                      value={form.classId}
                      onChange={handleField}
                      required
                      disabled={loadingClasses}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10 disabled:bg-gray-50 disabled:text-[#636363]"
                    >
                      <option value="">{loadingClasses ? "Loading classes…" : "Select your class"}</option>
                      {classes.map((c) => (
                        <option key={c.id} value={c.id}>{classLabel(c)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#1F1F1F]">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleField}
                      placeholder="you@example.com"
                      required
                      className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#1F1F1F]">
                      Phone Number <span className="font-normal text-[#636363]">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleField}
                      placeholder="+91 98765 43210"
                      className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#1F1F1F]">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={form.password}
                        onChange={handleField}
                        placeholder="Min 8 chars, uppercase, number"
                        required
                        className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-10 text-sm text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#636363] hover:text-[#1F1F1F]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-[#636363]">At least 8 characters, one uppercase, one number</p>
                  </div>

                  {error && (
                    <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0056D2] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#004BB5] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send OTP <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="mb-6 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#0056D2]/10">
                    <Mail className="h-7 w-7 text-[#0056D2]" />
                  </div>
                  <h1 className="text-2xl font-extrabold text-[#1F1F1F]">Check your email</h1>
                  <p className="mt-1.5 text-sm text-[#636363]">
                    We sent a 6-digit code to <span className="font-semibold text-[#1F1F1F]">{form.email}</span>
                  </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-6">
                  {/* OTP inputs */}
                  <div>
                    <label className="mb-3 block text-center text-sm font-semibold text-[#1F1F1F]">Enter verification code</label>
                    <div className="flex justify-center gap-3" onPaste={handleOtpPaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => { otpRefs.current[i] = el; }}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpInput(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          className="h-12 w-11 rounded-xl border-2 border-gray-200 text-center text-lg font-bold text-[#1F1F1F] outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/10"
                        />
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0056D2] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#004BB5] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & Create Account <CheckCircle2 className="h-4 w-4" /></>}
                  </button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-[#636363] hover:text-[#1F1F1F] transition"
                    >
                      ← Change details
                    </button>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || loading}
                      className="flex items-center gap-1.5 text-[#0056D2] hover:underline disabled:opacity-50 disabled:no-underline transition"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-[#636363]">
            By signing up, you agree to Phroneo&apos;s Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
